import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { throttle } from 'lodash';
import onelps from '../../api';
import { AppContext } from '../../Store';
import { NicknameFieldContext } from '../../state-management/contexts/regform-fields/nickname-field-context';
import { PasswordFieldContext } from '../../state-management/contexts/regform-fields/password-field-context';
import { EmailFieldContext } from '../../state-management/contexts/regform-fields/email-field-context';
import { TermsCheckboxContext } from '../../state-management/contexts/regform-checkboxes/terms-checkbox-context';
import { OneLPSsdkParamsContext } from '../../onelps-sdk-params-context';
import {
	isEmailInvalidFormat,
	isValidationErrorResponse, isResponseWithSuggestions,
	handleNicknameValidity, handleEmailValidity, handleNicknameSuggestions,
	handlePasswordValidity, handlePasswordValidityWithInvalidNickname,
} from './common';
import { useTranslation } from 'react-i18next';
import { DEFAULT_ERROR_CODE } from '../server-messages';

export const withInlineValidation = function(Component) {

	const EnhancedComponent = function({ ...props }) {
		const ERROR_DELAY_FOCUS = 250;
		const [errDelay, setErrDelay] = useState(0);
		const [nicknameState, nicknameDispatch] = useContext(NicknameFieldContext);
		const passwordDispatch = useContext(PasswordFieldContext)[1];
		const emailDispatch = useContext(EmailFieldContext)[1];
		const termsDispatch = useContext(TermsCheckboxContext)[1];
		const onelpsParams = useContext(OneLPSsdkParamsContext);
		const [state, dispatch] = useContext(AppContext);
		const isMounted = useRef(true);
		const { t } = useTranslation('translation');

		const [showNicknameLoadingSpinner, setShowNicknameLoadingSpinner] = useState(false);
		const [showPasswordLoadingSpinner, setShowPasswordLoadingSpinner] = useState(false);
		const [showEmailLoadingSpinner, setShowEmailLoadingSpinner] = useState(false);

		useEffect(() => {
			isMounted.current = true;
			// bla bla do something each time this components changes, the props change or something

			// return means cleanup at the end
			return () => {
				isMounted.current = false;
			};
		}, []);

		const shouldValidateEmailFormat = onelpsParams.form.validateEmailFormat;

		const validatePassword = async (passwordValue) => {
			setShowPasswordLoadingSpinner(true);

			try {
				const validateRequest = { password: passwordValue };
				const validationResult = await onelps.actions.validate(validateRequest, state.locale);

				const nicknameValid = !nicknameState.error.message;

				if (isValidationErrorResponse(validationResult)) {
					const invalidResultCallbackFn = () => {
						setErrDelay(ERROR_DELAY_FOCUS);
						setShowPasswordLoadingSpinner(false);
					};

					if (nicknameValid) {
						handlePasswordValidity(validationResult.errors.password, passwordDispatch, invalidResultCallbackFn);
					} else {
						handlePasswordValidityWithInvalidNickname(validationResult.errors.password, passwordDispatch, invalidResultCallbackFn);
					}

				} else {
					setShowPasswordLoadingSpinner(false);

					passwordDispatch({ type: 'HIDE_PASSWORD_ERROR' });
					passwordDispatch({ type: 'REMOVE_PASSWORD_ERROR' });
					passwordDispatch({ type: 'SET_PASSWORD_VALIDITY', payload: true });
				}
			} catch (e) {
				setShowPasswordLoadingSpinner(false);

				dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
				dispatch({ type: 'SHOW_SERVER_ERROR' });
			}
		};

		const validatePasswordWithoutNickname = async (passwordValue) => {
			setShowPasswordLoadingSpinner(true);

			try {
				const validateRequest = { password: passwordValue };
				const validationResult = await onelps.actions.validate(validateRequest, state.locale);

				if (isValidationErrorResponse(validationResult)) {
					const invalidResultCallbackFn = () => {
						setErrDelay(ERROR_DELAY_FOCUS);
						setShowPasswordLoadingSpinner(false);
					};

					handlePasswordValidity(validationResult.errors.password, passwordDispatch, invalidResultCallbackFn);
				} else {
					setShowPasswordLoadingSpinner(false);

					passwordDispatch({ type: 'HIDE_PASSWORD_ERROR' });
					passwordDispatch({ type: 'REMOVE_PASSWORD_ERROR' });
					passwordDispatch({ type: 'SET_PASSWORD_VALIDITY', payload: true });
				}
			} catch (e) {
				setShowPasswordLoadingSpinner(false);

				dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
				dispatch({ type: 'SHOW_SERVER_ERROR' });
			}
		};

		const validateNickname = async (nicknameValue) => {
			setShowNicknameLoadingSpinner(true);

			try {
				const validateRequest = { nickname: nicknameValue };
				const validationResult = await onelps.actions.validate(validateRequest, state.locale);

				if (isValidationErrorResponse(validationResult)) {
					const invalidResultCallbackFn = () => {
						setShowNicknameLoadingSpinner(false);
						setErrDelay(ERROR_DELAY_FOCUS);
					};

					handleNicknameValidity(validationResult.errors.nickname, nicknameDispatch, invalidResultCallbackFn);

					if (isResponseWithSuggestions(validationResult)) {
						handleNicknameSuggestions(validationResult, nicknameDispatch);
					}
				} else {
					setShowNicknameLoadingSpinner(false);
					nicknameDispatch({ type: 'HIDE_NICKNAME_ERROR' });
					nicknameDispatch({ type: 'REMOVE_NICKNAME_ERROR' });
					nicknameDispatch({ type: 'SET_NICKNAME_VALIDITY', payload: true });
				}
			} catch (e) {
				setShowNicknameLoadingSpinner(false);

				dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
				dispatch({ type: 'SHOW_SERVER_ERROR' });
			}
		};

		const validateEmail = async (emailValue) => {
			if (shouldValidateEmailFormat && isEmailInvalidFormat(emailValue)) {
				emailDispatch({ type: 'SET_EMAIL_ERROR', payload: t('form.email.errorMessages.errorMessageInvalid') });
				emailDispatch({ type: 'SHOW_EMAIL_ERROR' });
				emailDispatch({ type: 'SET_EMAIL_VALIDITY', payload: false });
			} else {
				try {
					setShowEmailLoadingSpinner(true);

					const validateRequest = { email: emailValue };
					const validationResult = await onelps.actions.validate(validateRequest, state.locale);

					if (isValidationErrorResponse(validationResult)) {
						const invalidResultCallbackFn = () => {
							setShowEmailLoadingSpinner(false);
							setErrDelay(ERROR_DELAY_FOCUS);
						};

						handleEmailValidity(validationResult.errors.email, emailDispatch, invalidResultCallbackFn);
					} else {
						setShowEmailLoadingSpinner(false);

						emailDispatch({ type: 'HIDE_EMAIL_ERROR' });
						emailDispatch({ type: 'REMOVE_EMAIL_ERROR' });
						emailDispatch({ type: 'SET_EMAIL_VALIDITY', payload: true });
					}
				} catch (e) {
					setShowEmailLoadingSpinner(false);

					dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
					dispatch({ type: 'SHOW_SERVER_ERROR' });
				}
			}
		};

		const delayedValidation = useCallback(
			throttle((validateFn, inputValue) => {
				validateFn(inputValue);
			}, 500),
			[]
		);

		return (
			<Component
				{...props}
				onNicknameChangeCallback={(e) => {
					const payload = e.target.value;
					nicknameDispatch({ type: 'RESET_NICKNAME_VALIDITY' });
					nicknameDispatch({ type: 'SET_NICKNAME_VALUE', payload });

					delayedValidation(validateNickname, payload);
				}}
				onPasswordChangeCallback={(e) => {
					passwordDispatch({ type: 'RESET_PASSWORD_VALIDITY' });
					const payload = e.target.value;

					passwordDispatch({ type: 'SET_PASSWORD_VALUE', payload });

					delayedValidation(validatePassword, payload);
				}}
				onPasswordWithoutNicknameChangeCallback={(e) => {
					passwordDispatch({ type: 'RESET_PASSWORD_VALIDITY' });
					const payload = e.target.value;

					passwordDispatch({ type: 'SET_PASSWORD_VALUE', payload });

					delayedValidation(validatePasswordWithoutNickname, payload);
				}}
				onEmailChangeCallback={(e) => {
					emailDispatch({ type: 'RESET_EMAIL_VALIDITY' });
					const payload = e.target.value;

					emailDispatch({ type: 'SET_EMAIL_VALUE', payload });
					delayedValidation(validateEmail, payload);
				}}
				errDelay={errDelay}
				onTermsCheckedCallback={(e) => {
					termsDispatch({ type: 'SET_TC_VALUE', payload: e.target.checked });
					if (e.target.checked) {
						termsDispatch({ type: 'HIDE_TC_ERROR' });
						termsDispatch({ type: 'REMOVE_TC_ERROR' });
					}
				}}
				showNicknameLoadingSpinner={showNicknameLoadingSpinner}
				showPasswordLoadingSpinner={showPasswordLoadingSpinner}
				showEmailLoadingSpinner={showEmailLoadingSpinner}
			/>
		);
	};
	EnhancedComponent.displayName = 'componentWithInlineValidation';
	return EnhancedComponent;
};

